import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { VirtualListModule } from '../angular-virtual-list';
import { ContactsTableComponent } from './contacts-table/contacts-table.component';
import { AccountsTableComponent } from './accounts-table/accounts-table.component';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { SelectedSuggestionPillDataModel } from '@omni/models/search-config-data-model';
import { AutoFocusSearchbarDirective } from '@omni/directives/autofocus.directive';
import { CapitalizeFirstLetter } from '@omni/pipes/common-pipes/capitalize-first-letter.pipe';

function pxToVh(px: number) {
  const windowHeight = window.innerHeight;
  return (px / windowHeight) * 100;
}

@Component({
  selector: 'go-app-customers-pane',
  templateUrl: './go-app-customers-pane.component.html',
  styleUrl: './go-app-customers-pane.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    VirtualListModule,
    AccountsTableComponent,
    ContactsTableComponent,
    AutoFocusSearchbarDirective,
    CapitalizeFirstLetter,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoAppCustomersPaneComponent {
  @Input() set rightPaneVisible(value: boolean) {
    if (value && this.selectedTab) {
      this.collapseCustomersPane();
    }
  }

  private _selectedTab: 'accounts' | 'contacts' | undefined;
  @Input() set selectedTab(value: 'accounts' | 'contacts') {
    if (value === this._selectedTab) {
      return;
    }
    if (!this._selectedTab) {
      this.expandCustomersPane();
    }
    this._selectedTab = value;
    if (!value) {
      this.searchBarVisible = false;
    }
    this._cd.markForCheck();
  }
  get selectedTab() {
    return this._selectedTab;
  }
  searchBarVisible: boolean = false;
  searchText: string = '';
  filterCount: number = 0;
  @ViewChild('contactsTable') contactsTable: ContactsTableComponent;
  @ViewChild('accountsTable') accountsTable: AccountsTableComponent;
  @ViewChild('content') content: ElementRef;
  hasSelection: boolean = false;
  private readonly _minTableHeight: number = 146;
  contentHeight = pxToVh(this._minTableHeight);

  @HostBinding('style.height.px')
  get height() {
    if (this.selectedTab) {
      return this._minTableHeight + 54;
    }
    return undefined;
  }

  get isResized() {
    return this.selectedTab && this.contentHeight > pxToVh(this._minTableHeight);
  }

  private get activeTable() {
    return this.selectedTab === 'accounts'
      ? this.accountsTable
      : this.contactsTable;
  }

  constructor(
    public contactService: ContactOfflineService,
    private readonly _el: ElementRef,
    private readonly _cd: ChangeDetectorRef,
  ) {}

  handleTabSelection(tab: 'accounts' | 'contacts' | undefined) {
    if (tab === this.selectedTab) {
      this.selectedTab = undefined;
      this.searchBarVisible = false;
      this._cd.markForCheck();
      return;
    }
    this.searchText = '';
    this.selectedTab = tab;
    this.filterCount = 0;
    this._cd.markForCheck();
  }

  handleSearchBarButtonClick() {
    this.searchText = '';
    this.searchBarVisible = !this.searchBarVisible;
    if (!this.selectedTab && this.searchBarVisible) {
      this.handleTabSelection('contacts');
    }
    this._cd.markForCheck();
  }

  handleAction(event: any, action: string) {
    this.activeTable.handleHeaderAction(action);
  }

  onSearch(event: any) {
    this.searchText = event.target.value;
    this._cd.markForCheck();
  }

  onFilterChanged(filters: SelectedSuggestionPillDataModel[]) {
    this.filterCount = filters?.length || 0;
    this._cd.markForCheck();
  }

  onSelectionChanged(selection: Set<string>) {
    this.hasSelection = selection.size > 0;
    this._cd.markForCheck();
  }

  private setContentHeight(height: number, animate: boolean = true) {
    const maxHeight = this.getMaxHeight();
    height = Math.min(maxHeight, Math.max(this._minTableHeight, height));
    this.contentHeight = pxToVh(height);
    this._cd.markForCheck();
    const content = this.content?.nativeElement as HTMLElement;
    if (!content) return;
    if (animate) {
      content.style.transition = 'height 0.3s ease-in-out';
      setTimeout(() => {
        content.style.removeProperty('transition');
      }, 300);
    } else {
      content.style.removeProperty('transition');
    }
  }

  @HostListener('document:mouseup', ['$event'])
  @HostListener('document:touchend', ['$event'])
  private collapseCustomersPane(event?: MouseEvent | TouchEvent) {
    const content = this._el.nativeElement as HTMLElement;
    const clickedElement = event?.target as HTMLElement;
    if (!content || !this.isResized) return;
    if (
      event &&
      (content.contains(clickedElement) ||
        clickedElement?.closest('ion-popover') ||
        (!clickedElement?.closest('ion-nav')?.querySelector('page-home') &&
          !clickedElement?.closest('go-app-activities-page')))
    )
      return;
    this.setContentHeight(this._minTableHeight);
  }

  private getMaxHeight() {
    const el = this._el.nativeElement as HTMLElement;
    const parent = el.closest('go-app-activities-page');
    if (!parent) return 0;
    return parent.getBoundingClientRect().height * 0.8;
  }

  private expandCustomersPane() {
    const maxHeight = this.getMaxHeight();
    this.setContentHeight(maxHeight);
  }

  onCustomersPaneMouseDown(event: MouseEvent | TouchEvent) {
    let resizeStarted = true;
    let mouseMoved = false;
    let startY =
      event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    const content = this.content.nativeElement as HTMLElement;
    if (!content) return;
    const contentRect = content.getBoundingClientRect();
    const contentHeight = contentRect.height;
    let startHeight = contentHeight;
    let minHeight = this._minTableHeight;
    let maxHeight = this.getMaxHeight();

    const updateEventHandlers = (addRemove: 'add' | 'remove') => {
      const fn =
        addRemove === 'add'
          ? document.addEventListener
          : document.removeEventListener;
      fn('mousemove', onCustomersPaneMouseMove);
      fn('mouseup', onCustomersPaneMouseUp);
      fn('touchmove', onCustomersPaneMouseMove);
      fn('touchend', onCustomersPaneMouseUp);
    };

    const onCustomersPaneMouseMove = (event: MouseEvent | TouchEvent) => {
      if (!resizeStarted) return;
      mouseMoved = true;
      const clientY =
        event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
      const height = startHeight + startY - clientY;
      this.setContentHeight(height, false);
    };

    const onCustomersPaneMouseUp = (event: MouseEvent | TouchEvent) => {
      resizeStarted = false;
      updateEventHandlers('remove');

      if (!mouseMoved) return;
      const clientY =
        event instanceof MouseEvent ? event.clientY : event.changedTouches[0]?.clientY || 0;
      const snapDistance = 100;
      let height = Math.min(
        maxHeight,
        Math.max(minHeight, startHeight + startY - clientY),
      );
      if (height > maxHeight - snapDistance) {
        this.expandCustomersPane();
      } else if (height < minHeight + snapDistance) {
        this.collapseCustomersPane();
      }
    };

    updateEventHandlers('add');
  }

  onCustomersPaneSwipeUp(event: any) {
    this.expandCustomersPane();
  }

  onCustomersPaneSwipeDown(event: any) {
    this.collapseCustomersPane();
  }
}
